import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Thumb } from '../components/ProductCard';
import { Qty } from '../components/Qty';
import { money, priceAt } from '../format';
import { useShop } from '../shop';
import { cartStore, useCart } from '../store';

interface DeliveryBand {
  upTo: number | null;
  fee: number;
}

function deliveryFee(rules: string | null, subtotal: number): number | null {
  if (!rules) return null;
  try {
    const bands = JSON.parse(rules) as DeliveryBand[];
    for (const b of bands) if (b.upTo === null || subtotal <= b.upTo) return b.fee;
    return null;
  } catch {
    return null;
  }
}

/**
 * Milestone 1 cart. The order itself (address, delivery, payment) is milestone
 * 2; until then the cart hands over to the chat, which is where the customer
 * came from: one tap opens WhatsApp with the list filled in.
 */
export function CartPage() {
  const { slug, shop, catalogue, session } = useShop();
  const cart = useCart(slug);

  const lines = useMemo(() => {
    const byVariant = new Map<number, { product: (typeof catalogue.products)[number]; variant: (typeof catalogue.products)[number]['variants'][number] }>();
    for (const p of catalogue.products) for (const v of p.variants) byVariant.set(v.id, { product: p, variant: v });
    return cart.map((l) => {
      const hit = byVariant.get(l.variantId);
      const unit = hit ? priceAt(hit.variant.price, hit.variant.breaks, l.qty) : null;
      return { ...l, hit, unit, total: unit === null ? null : unit * l.qty };
    });
  }, [cart, catalogue]);

  const stale = lines.filter((l) => !l.hit);
  const live = lines.filter((l) => l.hit);
  const subtotal = live.reduce((s, l) => s + (l.total ?? 0), 0);
  const priced = session.pricesShown && live.every((l) => l.total !== null);
  const fee = priced ? deliveryFee(shop.deliveryRules, subtotal) : null;
  const belowMin = priced && shop.minOrderDefault > 0 && subtotal < shop.minOrderDefault;
  const phone = shop.contactPhone?.replace(/\D/g, '') ?? '';

  const message = useMemo(() => {
    const items = live.map((l) => {
      const name = l.hit!.product.variants.length > 1 ? `${l.hit!.product.name} (${l.hit!.variant.name})` : l.hit!.product.name;
      const sku = l.hit!.variant.sku ?? l.hit!.product.sku;
      return `• ${name}${sku ? ` [${sku}]` : ''} × ${l.qty}${l.total !== null && priced ? ` — ${money(l.total, shop.currency)}` : ''}`;
    });
    const head = session.contactName ? `Hi, this is ${session.contactName}. I'd like to order:` : "Hi, I'd like to order:";
    const foot = priced ? `\nSubtotal ${money(subtotal, shop.currency)}` : '';
    return `${head}\n${items.join('\n')}${foot}\n\nfrom ${shop.name} shop.`;
  }, [live, priced, subtotal, shop, session.contactName]);

  const waHref = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : null;

  return (
    <>
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-40 pt-4">
        <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">Your cart</h1>

        {lines.length === 0 ? (
          <div className="py-16 text-center text-stone-500">
            Nothing here yet.
            <div className="mt-4">
              <Link to={`/${slug}`} className="rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white">Browse the shop</Link>
            </div>
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-stone-200 dark:divide-stone-800">
            {live.map((l) => {
              const { product, variant } = l.hit!;
              return (
                <li key={l.variantId} className="flex gap-3 py-3">
                  <Link to={`/${slug}/p/${product.slug}`} className="shrink-0">
                    <Thumb src={product.images[0]} alt={product.name} className="size-16 rounded-lg" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/${slug}/p/${product.slug}`} className="line-clamp-2 text-sm font-medium text-stone-900 dark:text-stone-100">
                      {product.name}
                    </Link>
                    {product.variants.length > 1 && <div className="text-xs text-stone-500">{variant.name}</div>}
                    {variant.inStock === false && <div className="text-xs text-amber-700 dark:text-amber-400">Out of stock now</div>}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <Qty compact value={l.qty} onChange={(n) => cartStore.setQty(slug, product.id, variant.id, n)} />
                      <div className="text-right">
                        {session.pricesShown && l.total !== null ? (
                          <>
                            <div className="text-sm font-semibold text-stone-900 dark:text-stone-100">{money(l.total, shop.currency)}</div>
                            {l.qty > 1 && l.unit !== null && <div className="text-xs text-stone-500">{money(l.unit, shop.currency)} each</div>}
                          </>
                        ) : (
                          <span className="text-xs text-stone-500">{session.pricesShown ? 'Price on request' : ''}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
            {stale.map((l) => (
              <li key={l.variantId} className="flex items-center justify-between py-3 text-sm text-stone-500">
                <span>An item that is no longer listed</span>
                <button type="button" onClick={() => cartStore.setQty(slug, l.productId, l.variantId, 0)} className="text-[var(--brand)]">
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {live.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-stone-200 bg-white/95 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 backdrop-blur dark:border-stone-800 dark:bg-stone-950/95">
          <div className="mx-auto max-w-3xl">
            {priced && (
              <dl className="mb-2 space-y-0.5 text-sm">
                <div className="flex justify-between text-stone-600 dark:text-stone-300">
                  <dt>Subtotal{shop.pricesIncludeTax ? ' (incl. tax)' : ''}</dt>
                  <dd>{money(subtotal, shop.currency)}</dd>
                </div>
                {fee !== null && (
                  <div className="flex justify-between text-stone-600 dark:text-stone-300">
                    <dt>Delivery</dt>
                    <dd>{fee === 0 ? 'Free' : money(fee, shop.currency)}</dd>
                  </div>
                )}
                {belowMin && <div className="text-xs text-amber-700 dark:text-amber-400">Minimum order is {money(shop.minOrderDefault, shop.currency)}.</div>}
              </dl>
            )}
            {shop.orderingPaused ? (
              <p className="text-center text-sm text-stone-500">{shop.pauseMessage || 'Not taking orders right now.'}</p>
            ) : waHref ? (
              <a
                href={waHref}
                target="_blank"
                rel="noopener"
                className={`flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] text-sm font-semibold text-white ${belowMin ? 'pointer-events-none opacity-50' : ''}`}
                aria-disabled={belowMin}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.2-.6.8-.8 1-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1.1 2.7c.1.2 1.9 2.9 4.6 4 1.7.7 2.4.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.5-.3z" /></svg>
                Send order on WhatsApp
              </a>
            ) : (
              <p className="text-center text-sm text-stone-500">Checkout is on its way. Send us your list in the chat for now.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
